import { AddCircle, CircleIcon } from 'src/resources/icons';
import { Avatar, Box, Divider, Flex, Text, useDisclosure, SimpleGrid } from '@chakra-ui/react';

import AlertCircleIcon from 'src/resources/icons/AlertCircleIcon';
import React, { useEffect, useState } from 'react'
import RightAlignedModal from './rightAlignedModal';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/common/constants/constantEnums';
import { ProfileService } from "src/rest/profileService";
import { GlobalUtils } from "src/utils/globalUtils";
import type { ProfileDto } from "src/types/Profile";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";
import { ConnectionsService } from "src/rest/connectionsService";

interface MarriageRegistrationProps {
  isHindiLabels?: boolean;
  isConsultant?: boolean;
}

export const MarriageRegistration: React.FC<MarriageRegistrationProps> = ({ isHindiLabels = false, isConsultant = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const [userProfile, setUserProfile] = useState<ProfileDto | null>(null);
  const [photoUrlsById, setPhotoUrlsById] = useState<Record<string, string>>({});
  const [pendingCountById, setPendingCountById] = useState<Record<string, number>>({});

  const profileCount = userProfile?.marriageProfiles?.length || 0;
  const consultantInfo = isConsultant ? GlobalUtils.getConsultantInfo() : null;
  const profileLimit = consultantInfo?.marriage_profile_limit ?? null;

  // For regular users: block after 1 profile. For consultants: block once limit is reached.
  const hasMarriageProfile = isConsultant
    ? profileLimit !== null && profileCount >= profileLimit
    : profileCount > 0;

  useEffect(() => {
    const load = async () => {
      try {
        const emailId = GlobalUtils.getUserLoginInfo()?.emailId || "";
        if (!emailId) return;
        const profile = await ProfileService.getProfile(emailId);
        setUserProfile(profile);
      } catch {
        setUserProfile(null);
      }
    };
    load();
  }, []);

  useEffect(() => {
    // Cleanup object URLs on unmount
    return () => {
      Object.values(photoUrlsById).forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          // ignore
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const profiles = userProfile?.marriageProfiles || [];
    if (!profiles.length) return;

    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        profiles.map(async (p) => {
          const id = String(p.id);
          const fileName = (p as any).profilePhoto as string | undefined;
          if (!id || !fileName) return null;

          try {
            const url = await fetchAuthedImageObjectUrl(fileName);
            return [id, url] as const;
          } catch {
            return null;
          }
        })
      );

      if (cancelled) {
        entries.forEach((e) => {
          if (e?.[1]) URL.revokeObjectURL(e[1]);
        });
        return;
      }

      setPhotoUrlsById((prev) => {
        const next = { ...prev };
        entries.forEach((e) => {
          if (!e) return;
          const [id, url] = e;
          // revoke previous url for same id
          if (next[id] && next[id] !== url) {
            try {
              URL.revokeObjectURL(next[id]);
            } catch {
              // ignore
            }
          }
          next[id] = url;
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [userProfile?.marriageProfiles]);

  // Fetch pending connection request counts for each marriage profile
  useEffect(() => {
    const profiles = userProfile?.marriageProfiles || [];
    if (!profiles.length) return;

    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        profiles.map(async (p) => {
          const id = String(p.id);
          try {
            const requests = await ConnectionsService.getRequestsByMarriageProfileId(p.id, "PENDING");
            return [id, Array.isArray(requests) ? requests.length : 0] as const;
          } catch {
            return [id, 0] as const;
          }
        })
      );

      if (cancelled) return;

      const countMap: Record<string, number> = {};
      entries.forEach(([id, count]) => { countMap[id] = count; });
      setPendingCountById(countMap);
    })();

    return () => { cancelled = true; };
  }, [userProfile?.marriageProfiles]);

  return (
    <Flex direction="column" justifyContent="flex-end" mt="2rem" w="full" mr={{ base: 0, md: "-1rem" }}>
      <Flex width="100%" justifyContent="flex-end">
        {hasMarriageProfile ? (
          <Flex
            margin={2}
            borderRadius="5px"
            background="rgba(108, 117, 125, 0.10)"
            gap="8px"
            padding="14px"
            opacity={0.75}
            cursor="not-allowed"
            role="status"
            aria-label="Marriage profile limit reached"
          >
            <AlertCircleIcon />
            <Text>
              {isConsultant
                ? `Profile limit reached (${profileCount}/${profileLimit})`
                : isHindiLabels
                ? "You already have a Marriage Profile Hindi"
                : "You already have a Marriage Profile"}
            </Text>
          </Flex>
        ) : (
          <Flex
            margin={2}
            onClick={() => {
              onOpen();
            }}
            borderRadius="5px"
            background="rgba(108, 117, 125, 0.10)"
            gap="8px"
            padding="14px"
            _hover={{ cursor: "pointer" }}
          >
            <AddCircle />
            <Text>
              {isConsultant
                ? `Add a Marriage Profile (${profileCount}/${profileLimit ?? "∞"})`
                : isHindiLabels
                ? "Add a Marriage Profile Hindi"
                : "Add a Marriage Profile"}
            </Text>
          </Flex>
        )}
      </Flex>

      {userProfile?.marriageProfiles?.length ? (
        <SimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={{ base: 3, md: 4 }}
          mt={{ base: 4, md: "1.5rem" }}
          w="full"
        >
          {userProfile.marriageProfiles.map((p) => {
            const isActive = Boolean((p as any).active);
            return (
              <Box
                key={p.id}
                w="full"
                bg="white"
                borderRadius="16px"
                boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)"
                border="1px solid rgba(108, 117, 125, 0.18)"
                p={4}
                opacity={isActive ? 1 : 0.7}
              >
                <Flex alignItems="center" mb={4}>
                  <Avatar
                    name={p.fullName || "Marriage Profile"}
                    src={photoUrlsById[String(p.id)]}
                    size="md"
                    mr={3}
                  />
                  <Flex direction="column" minW={0}>
                    <Text fontWeight="600" color="#1F0812" noOfLines={1}>
                      {p.fullName || "Marriage Profile"}
                    </Text>
                    <Text fontSize="sm" color="#6C757D" noOfLines={1}>
                      {p.maritalStatus || ""}
                    </Text>
                  </Flex>
                </Flex>

                <Flex alignItems="center" mb={2}>
                  <CircleIcon color={isActive ? "#E35E00" : "#6C757D"} />
                  <Text ml={2} fontSize="sm" fontWeight="500" color={isActive ? "#E35E00" : "#6C757D"}>
                    {isActive ? "Active" : "Inactive"}
                  </Text>
                </Flex>

                <Flex alignItems="center">
                  <AlertCircleIcon />
                  <Text ml={2} fontSize="sm" color="#6C757D">
                    {(() => {
                      const count = pendingCountById[String(p.id)] ?? null;
                      if (count === null) return "Loading requests...";
                      if (count === 0) return "No new contact requests.";
                      return `You have received ${count} new contact request${count === 1 ? "" : "s"}.`;
                    })()}
                  </Text>
                </Flex>

                <Divider my={4} />

                <Text
                  textAlign="center"
                  fontWeight="600"
                  color="#1F0812"
                  _hover={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    navigate(`${AppRoutes.MATRIMONY_PROFILE_DETAILS}?id=${encodeURIComponent(String(p.id))}`);
                  }}
                >
                  Manage Profile
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>
      ) : null}

      <RightAlignedModal isOpen={isOpen} onClose={onClose} view={"MARRIAGE_REGISTRATION"} userData={userProfile} isConsultant={isConsultant} />
    </Flex>
  )
}
